import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { type MouseEvent, type ReactElement } from 'react';
import { Link, generatePath, useLocation, useNavigate } from 'react-router-dom';
import { ImportCurve, Eye, MoreVertical, Trash, User } from '@/components/icons';
import { PatientAvatar } from '@/components/patients/PatientAvatar';
import { listTableStyles } from '@/components/system/listTableStyles';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EMPTY_VALUE } from '@/constants';
import { PatientDetailPage } from '@/pages/constants';
import { canExportReport, getAge, getReportLink, translateGender } from '@/utils';
import { ReportItemProps } from './types';

function ReportTableItem({
    report,
    reportStatus,
    statusInfo,
    onExport,
    onDelete,
}: ReportItemProps): ReactElement {
    const navigate = useNavigate();
    const location = useLocation();
    const patientId = report.patient?.id;
    const backTo = `${location.pathname}${location.search}`;
    const exportEnabled = canExportReport(report);
    const pendingConsolidationsCount = report.pending_consolidations?.length ?? 0;
    const pendingPublisherApprovalsCount = report.pending_publisher_result_approvals_count ?? 0;
    const pendingReviewCount = pendingConsolidationsCount + pendingPublisherApprovalsCount;

    return (
        <motion.tr
            className={`cursor-pointer border-b ${listTableStyles.row}`}
            variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0 },
            }}
            onClick={() => {
                if (!report?.id) return;
                navigate(getReportLink(report.id));
            }}
        >
            <td className={listTableStyles.cell}>
                <div className="group flex items-center gap-3">
                    <PatientAvatar
                        name={report.patient?.full_name || ''}
                        avatarUrl={report.patient?.avatar_url}
                        className="size-10 rounded-lg"
                    />
                    <div className="min-w-0">
                        {patientId ? (
                            <Link
                                to={`/${generatePath(PatientDetailPage, {
                                    patientId,
                                })}`}
                                state={{ backTo }}
                                onClick={(event: MouseEvent) => event.stopPropagation()}
                                className="inline-flex"
                            >
                                <h3 className="font-semibold text-foreground transition-colors group-hover:text-brand">
                                    {report.patient?.full_name || 'Laudo sem título'}
                                </h3>
                            </Link>
                        ) : (
                            <h3 className="font-semibold text-foreground">
                                {report.patient?.full_name || 'Laudo sem título'}
                            </h3>
                        )}
                        {pendingReviewCount > 0 && (
                            <Badge variant="warning" className="mt-1 px-2 py-0 text-[11px]">
                                {pendingReviewCount} resultado
                                {pendingReviewCount > 1 ? 's' : ''} p/ revisão
                            </Badge>
                        )}
                    </div>
                </div>
            </td>
            <td className={listTableStyles.cell}>
                <Badge variant="outline" className={`${statusInfo?.color} border`}>
                    {statusInfo?.label || reportStatus}
                </Badge>
            </td>
            <td className={`${listTableStyles.subtleCell} tabular-nums`}>
                {report.updated_at
                    ? format(new Date(report.updated_at), 'dd/MM/yyyy')
                    : EMPTY_VALUE}
            </td>
            <td className={`${listTableStyles.subtleCell} tabular-nums`}>
                {report.patient?.date_of_birth
                    ? `${getAge(report.patient?.date_of_birth)} anos`
                    : `${EMPTY_VALUE}`}
            </td>
            <td className={`${listTableStyles.subtleCell} capitalize`}>
                {translateGender(report.patient?.gender)}
            </td>
            <td className={listTableStyles.cell}>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={(event: MouseEvent) => event.stopPropagation()}
                        >
                            <MoreVertical className="size-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        align="end"
                        onClick={(event: MouseEvent) => event.stopPropagation()}
                    >
                        <DropdownMenuItem asChild>
                            <Link
                                to={getReportLink(report?.id)}
                                onClick={(event: MouseEvent) => event.stopPropagation()}
                            >
                                <Eye className="mr-2 size-4" />
                                Abrir laudo
                            </Link>
                        </DropdownMenuItem>
                        {exportEnabled && (
                            <DropdownMenuItem
                                onClick={(event: MouseEvent) => {
                                    event.stopPropagation();
                                    onExport();
                                }}
                                className="justify-between gap-3"
                            >
                                <span className="inline-flex items-center">
                                    <ImportCurve className="mr-2 size-4" />
                                    Baixar em .docx
                                </span>
                            </DropdownMenuItem>
                        )}
                        {patientId && (
                            <DropdownMenuItem asChild>
                                <Link
                                    to={`/${generatePath(PatientDetailPage, {
                                        patientId,
                                    })}`}
                                    state={{ backTo }}
                                    onClick={(event: MouseEvent) => event.stopPropagation()}
                                >
                                    <User className="mr-2 size-4" />
                                    Abrir paciente
                                </Link>
                            </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                            onClick={(event: MouseEvent) => {
                                event.stopPropagation();
                                onDelete();
                            }}
                            className="text-red-600 focus:text-red-600"
                        >
                            <Trash className="mr-2 size-4" />
                            Excluir
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </td>
        </motion.tr>
    );
}

export default ReportTableItem;
